import type { Paths } from '@src/utils/types';
import { useMemo, type ReactNode } from 'react';
import { Alert } from '../../../../Alert';
import { AlertVariants } from '../../../../Alert/types';
import { Box, HStack } from '../../../../Core';
import { FormGroup } from '../../../../Form/FormGroup';
import { Text } from '../../../../Text';
import type { EntityAdminRecord } from '../../../types';
import { filterAndMapGroupedDrawerOptions, type MappedDrawerOption } from '../filterAndMapDrawerOptions';
import type { InputsAndDropdownsDrawerOptions } from '../types';
import { getGuideTextValue, getTooltipValue } from '../utils';
import { DividerContent } from './DividerContent';
import { isOptionDivider } from './dividerUtils';
import { DrawerOptionContent } from './DrawerOptionContent';

// Loose record types match the runtime shape returned by useEntityAdminFormState. We avoid the
// strict Paths<TForm> shape here so this component is usable by both editing drawers and workflow
// step forms without forcing each caller to widen their state types.
interface DrawerOptionsListProps<TForm extends EntityAdminRecord> {
  drawerOptions: InputsAndDropdownsDrawerOptions<TForm>;
  form: TForm;
  originalEntity: TForm | undefined;
  inheritedValues: Record<Paths<TForm>, string | undefined>;
  errorState: Record<string, string | undefined> | null | undefined;
  touchState: Record<string, boolean> | null | undefined;
  onFormUpdate: (field: Paths<TForm>, value: any, suppressTouch?: boolean) => void;
  isEditing: boolean;
  isGuideVisible: boolean;
}

/**
 * Renders a list of drawer options (singles, grouped flex rows, dividers) with consistent
 * spacing, FormGroup wiring, and guide-text rendering. Shared between `InputsAndDropdownsDrawer`
 * (entity edit drawers) and `WorkflowFormStep` (multi-step workflow drawers).
 */
export function DrawerOptionsList<TForm extends EntityAdminRecord>({
  drawerOptions,
  form,
  originalEntity,
  inheritedValues,
  errorState,
  touchState,
  onFormUpdate,
  isEditing,
  isGuideVisible,
}: DrawerOptionsListProps<TForm>) {
  const filtered = useMemo(
    () => filterAndMapGroupedDrawerOptions(drawerOptions, form, originalEntity, inheritedValues),
    [drawerOptions, form, originalEntity, inheritedValues]
  );

  const renderGuideText = (guideText: ReactNode) => (
    <Box mt="spacing6" mb="spacing16" data-testid="form-group-guide-text">
      <Alert variant={AlertVariants.Guide} p="spacing8" dismissable={false}>
        {typeof guideText === 'string' ? <Text color="colorTextDefault">{guideText}</Text> : guideText}
      </Alert>
    </Box>
  );

  const getGroupGuideText = (group: MappedDrawerOption<TForm>[]) => {
    if (!isGuideVisible) {
      return undefined;
    }
    for (const { option } of group) {
      if (!isOptionDivider(option)) {
        const guideText = getGuideTextValue(option, form);
        if (guideText) {
          return guideText;
        }
      }
    }
    return undefined;
  };

  const renderDrawerOption = (
    { option, inheritedValue, key, label }: MappedDrawerOption<TForm>,
    suppressGuideText?: boolean
  ) => {
    if (isOptionDivider(option)) {
      return (
        <Box key={key} my="spacing12">
          <DividerContent title={option.title} />
        </Box>
      );
    }

    const content = (
      <DrawerOptionContent
        key={key}
        drawerOption={option}
        form={form}
        isEditing={isEditing}
        onFormUpdate={onFormUpdate}
        inheritedValue={inheritedValue}
        error={errorState?.[option.field]}
        touched={touchState?.[option.field] ?? false}
      />
    );

    if (option.useCustomFormGroup) {
      return content;
    }

    // Only show errors once the field has been touched (matches Field behavior).
    const errorIfTouched = touchState?.[option.field] ? errorState?.[option.field] : undefined;
    const tooltipValue = getTooltipValue(option, form);
    const guideTextValue = isGuideVisible && !suppressGuideText ? getGuideTextValue(option, form) : undefined;

    return (
      <FormGroup
        key={key}
        tooltip={tooltipValue}
        label={label}
        error={errorIfTouched}
        useErrorBorder
        guideText={guideTextValue}
      >
        {content}
      </FormGroup>
    );
  };

  return (
    <>
      {filtered.map((optionOrGroup, index) => {
        if (Array.isArray(optionOrGroup)) {
          const groupGuideText = getGroupGuideText(optionOrGroup);
          return (
            <Box key={`group-${index}`}>
              <HStack gap="spacing16" alignItems="flex-start" mb={groupGuideText ? 0 : 'spacing16'}>
                {optionOrGroup.map(mappedOption => (
                  <Box key={mappedOption.key} flex={1} minWidth={0}>
                    {renderDrawerOption(mappedOption, true)}
                  </Box>
                ))}
              </HStack>
              {groupGuideText ? renderGuideText(groupGuideText) : null}
            </Box>
          );
        }
        return renderDrawerOption(optionOrGroup);
      })}
    </>
  );
}
